import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  model,
} from '@angular/core';
import { ButtonComponent } from '../button/button.component';
import { DividerComponent } from '../divider/divider.component';
import { IconComponent } from '../icon/icon.component';
import { LogoComponent } from '../logo/logo.component';
import { LinkComponent } from '../link/link.component';
import { NavItemComponent } from '../nav-item/nav-item.component';
import type { IconName } from '@fortawesome/angular-fontawesome';
import { COPY_RIGHT_INFO } from '../footer-copyright/footer-copyright.constants';
import type { SidebarNavItem } from './sidebar-nav-item.model';

type SidebarNavRow = {
  readonly trackId: string;
  readonly item: SidebarNavItem;
};

@Component({
  selector: 'ps-sidebar',
  standalone: true,
  imports: [
    ButtonComponent,
    DividerComponent,
    IconComponent,
    LogoComponent,
    LinkComponent,
    NavItemComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'ps-sidebar',
    '[class.ps-sidebar-collapsed]': 'collapsed()',
    role: 'complementary',
    '[attr.aria-label]': 'ariaLabel()',
    '[attr.data-cy]': 'dataCy()',
    '(document:keydown.escape)': 'onDocumentEscape()',
  },
})
export class SidebarComponent {
  protected readonly contactRef: string = COPY_RIGHT_INFO.CONTACT.REF;
  protected readonly contactLabel: string = COPY_RIGHT_INFO.CONTACT.LABEL;

  public readonly collapsed = model(false);

  /** Unique segment for nav / toggle / help ids (`ps-sidebar-nav-{uuid}`). */
  private readonly _sidebarId = crypto.randomUUID();

  /** Landmark label for the sidebar shell (complementary region). */
  public readonly ariaLabel = input<string>('Application sidebar');
  /** Accessible name for the primary navigation area (projected links). */
  public readonly navigationAriaLabel = input<string>('Primary navigation');
  public readonly toggleCollapseLabel = input<string>('Collapse sidebar');
  public readonly toggleExpandLabel = input<string>('Expand sidebar');
  /** Optional footer landmark label; set `null` to omit and rely on native footer semantics. */
  public readonly footerAriaLabel = input<string | null>('Support');
  /** Accessible label for the mobile overlay dismiss control (tap outside the drawer). */
  public readonly backdropDismissLabel = input<string>('Close menu');
  /** Primary (top) navigation links. */
  public readonly staticNavItems = input<SidebarNavItem[]>([]);
  /** Secondary navigation links, separated from `staticNavItems` by a divider when both are non-empty. */
  public readonly dynamicNavItems = input<SidebarNavItem[]>([]);

  protected readonly navRegionId = computed(
    () => `ps-sidebar-nav-${this._sidebarId}`,
  );

  protected readonly toggleId = computed(() => `${this.navRegionId()}-toggle`);

  protected readonly helpSectionTitleId = computed(
    () => `${this.navRegionId()}-help-title`,
  );

  protected readonly arrowIcon = computed<IconName>(() =>
    this.collapsed() ? 'arrow-right' : 'arrow-left',
  );

  protected readonly toggleAriaLabel = computed(() =>
    this.collapsed() ? this.toggleExpandLabel() : this.toggleCollapseLabel(),
  );

  protected readonly supportTelUri = computed(
    () => `tel:${this.contactRef.replace(/\s/g, '')}`,
  );

  protected readonly supportPhoneAriaLabel = computed(
    () =>
      `Call ${COPY_RIGHT_INFO.COMPANY_NAME} support at ${this.contactLabel}`,
  );

  protected readonly dataCy = computed(() =>
    this.collapsed() ? 'ps-sidebar-collapsed' : 'ps-sidebar-expanded',
  );

  protected readonly navItemLayout = computed(() =>
    this.collapsed() ? 'compact' : 'default',
  );

  protected readonly showNavDivider = computed(
    () => this.staticNavItems().length > 0 && this.dynamicNavItems().length > 0,
  );

  protected readonly staticNavRows = computed((): SidebarNavRow[] =>
    this.staticNavItems().map((item, index) => ({
      trackId: `${index}-${item.href}`,
      item,
    })),
  );

  protected readonly dynamicNavRows = computed((): SidebarNavRow[] =>
    this.dynamicNavItems().map((item, index) => ({
      trackId: `${index}-${item.href}`,
      item,
    })),
  );

  protected onToggleCollapsed(): void {
    this.collapsed.update((v) => !v);
  }

  protected onDismissOverlay(): void {
    this.collapsed.set(true);
  }

  protected onDocumentEscape(): void {
    if (!this.collapsed()) {
      this.collapsed.set(true);
    }
  }
}
